<template>
  <div class="mb-10 flex size-full flex-col items-center md:max-w-7xl md:px-5 lg:px-10">
    <div class="mb-3 flex w-full items-center justify-between px-2 md:px-0">
      <p class="flex flex-col self-end text-xl text-gray-700 md:text-2xl lg:text-3xl">
        <span>Grupos de Productos</span>
        <span class="mt-1 text-sm font-light md:text-base"> Gestiona las variaciones de tus productos </span>
      </p>
      <button
        v-if="!selectedProductGroup?.id && !showCreateDialog"
        class="flex flex-row items-center space-x-2 rounded-md border border-lk-green bg-white p-2 text-sm text-lk-green hover:bg-lk-green hover:text-white md:text-base"
        @click="(showCreateDialog = true)"
      >
        <PlusIcon class="size-4 shrink-0" />
        <p>Crear Grupo</p>
      </button>
    </div>
    <div class="flex size-full flex-col rounded-md bg-white px-2 py-3 text-gray-700 md:px-4 md:py-5 lg:px-10">
      <div class="mb-3 flex w-full items-center">
        <button
          v-if="selectedProductGroup?.id"
          class="mb-3 flex items-center gap-2 rounded-full border border-gray-300 px-2 py-1 text-xs text-gray-500 hover:border-gray-500 hover:text-gray-700 md:mb-5 md:text-sm"
          @click="resetProductGroup"
        >
          <ChevronLeftIcon class="size-3 md:size-4" />
          <span>Volver</span>
        </button>
        <button
          v-if="showCreateDialog"
          class="mb-3 flex items-center gap-2 rounded-full border border-gray-300 px-2 py-1 text-xs text-gray-500 hover:border-gray-500 hover:text-gray-700 md:mb-5 md:text-sm"
          @click="(showCreateDialog = false)"
        >
          <ChevronLeftIcon class="size-3 md:size-4" />
          <span>Volver</span>
        </button>
        <div class="ml-auto flex space-x-2"></div>
      </div>
      <lokal-loading
        v-if="isProductGroupsLoading"
        v-model="isProductGroupsLoading"
      />
      <div
        v-else-if="showCreateDialog"
        class="w-full"
      >
        <maker-product-group-create-form
          v-model="showCreateDialog"
          :maker="maker"
        />
      </div>
      <template
        v-for="productGroup in productGroups"
        v-else
        :key="productGroup.id"
      >
        <maker-product-group-list-card
          v-if="!selectedProductGroup?.id || selectedProductGroup?.id === productGroup.id"
          :selected-product-group="selectedProductGroup"
          :product-group="productGroup"
          :maker="maker"
          class="mb-2"
          @set:product-group="(group: ProductGroup) => (selectedProductGroup = group)"
          @unset:product-group="() => (selectedProductGroup = {} as ProductGroup)"
        />
      </template>
      <div
        v-if="showProducts"
        class="mt-4 flex w-full flex-col"
      >
        <div class="mb-3 flex items-center justify-between">
          <p class="text-base font-medium md:text-lg">Productos en "{{ selectedProductGroup.name }}"</p>
          <p class="ml-auto text-sm text-gray-500 md:text-base">{{ totalResources }} productos</p>
        </div>
        <div class="mb-3 flex items-center justify-between">
          <p class="text-sm md:text-base">Busca por nombre o SKU:</p>
        </div>
        <Field
          v-model="shadowSearchText"
          name="shadowSearchText"
          class="mb-3 rounded-md border border-lk-light-gray p-2 text-sm focus:outline-none md:text-base"
          placeholder="Buscar productos..."
        />
        <lokal-loading
          v-if="productQueryIsLoading"
          v-model="productQueryIsLoading"
        />
        <template v-else>
          <maker-product-group-list-product-card
            v-for="product in products"
            :key="`product-group-product-${product.id}`"
            :product="product"
            :product-group="selectedProductGroup"
          />
          <pagination-component
            v-if="products?.length && totalPages > 1"
            :total-pages="totalPages"
            :current-page="currentPage"
            @set-new-page="updatePage"
          />
        </template>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { Field } from 'vee-validate';
import { debounce } from 'lodash';
import MakerProductGroupListCard from './maker-product-group-list-card.vue';
import MakerProductGroupListProductCard from './maker-product-group-list-product-card.vue';
import PaginationComponent from './shared/pagination-component.vue';
import MakerProductGroupCreateForm from './maker-product-group-create-form.vue';
import useProductGroupsQuery from './queries/product-groups-query';
import useProductsQuery from './queries/products-query';
import { ChevronLeftIcon } from '@heroicons/vue/24/solid';
import { PlusIcon } from '@heroicons/vue/24/outline';

const PAGE_SIZE = 24;
const DEBOUNCE_TIME = 200;

const props = defineProps<{
  maker: Maker;
}>();

const selectedProductGroup = ref({} as ProductGroup);
const showProducts = computed(() => !!selectedProductGroup.value?.id);
const showCreateDialog = ref(false);

const makerId = computed(() => props.maker.id);
const productGroupsQueryParams = computed(() => 'q[s][]=maker_priority+asc');
const { productGroups, isProductGroupsLoading } = useProductGroupsQuery(
  makerId,
  productGroupsQueryParams,
  computed(() => true),
);

const currentPage = ref(1);
const shadowSearchText = ref('');
const searchText = ref('');

const debounceIsTyping = debounce(() => {
  searchText.value = shadowSearchText.value;
  currentPage.value = 1;
}, DEBOUNCE_TIME);

watch(shadowSearchText, debounceIsTyping);

const queryParams = computed(() => {
  let params = `q[maker_id_eq]=${props.maker.id}&q[s][]=status+desc&q[s][]=maker_priority+asc&`;
  if (selectedProductGroup.value?.id) {
    params += `q[product_group_id_eq]=${selectedProductGroup.value.id}&`;
  }
  if (searchText.value) params += `search_text=${searchText.value}&`;

  return new URLSearchParams(params).toString();
});

const pageSize = computed(() => PAGE_SIZE);
const productQueryEnabled = computed(() => showProducts.value);

const { products, productQueryIsLoading, totalResources, totalPages } = useProductsQuery(
  currentPage,
  queryParams,
  productQueryEnabled,
  pageSize,
);

function resetProductGroup() {
  selectedProductGroup.value = {} as ProductGroup;
  currentPage.value = 1;
  searchText.value = '';
  shadowSearchText.value = '';
}

function updatePage(page: number) {
  currentPage.value = page;
}

watch(selectedProductGroup, () => {
  currentPage.value = 1;
});
</script>
